import React, { useEffect, useState } from 'react';
import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Chip,
  ChipProps,
  Stack,
  StackProps,
  TextField,
  Typography,
  TypographyProps,
} from '@mui/material';

import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import { useAppSelector } from '../../../../../hooks';
import { getQuestionDifficultyChipColor } from '../../../../../helper';
import { TP_COLORS } from '../../../../../constants';
import {
  getQuestionState,
  IQuestion,
} from '../../../../../store/QuestionSlice';
import { getAIMarking } from '../../../../../store/QuestionSlice/QuestionApi';
import { getAccessLimitState } from '../../../../../store/AccessLimitSlice';
import TableLoader from '../../../../../components/Loaders/TableLoader';
import PracticeQuestionLastPage from './PracticeQuestionLastPage';
import MarkdownRenderer from '../../../../../helper/MarkdownRenderer/MarkdownRenderer';
import { getAuthState } from '../../../../../store/AuthSlice';
import { saveStudentProgress } from '../../../../../store/ProgressSlice';

interface TheoryQuestionsProps {
  questionList: IQuestion[];
}

const boxProps: BoxProps = {
  padding: '20px',
  sx: {
    borderRadius: '15px',
    background: TP_COLORS.white,
  },
};

const topStackProps: StackProps = {
  direction: 'row',
  justifyContent: 'space-between',
  marginBottom: '20px',
};

const chipStyles: ChipProps['style'] = {
  height: '27px',
  fontWeight: 600,
  borderRadius: '5px',
};

const marksChipStyles: ChipProps['style'] = {
  ...chipStyles,
  backgroundColor: TP_COLORS.gray.dark,
  color: TP_COLORS.white,
  marginLeft: '10px',
};

const questionNoStackProps: StackProps = {
  flexDirection: 'row',
  justifyContent: 'center',
  position: 'relative',
  top: '35px',
  sx: { display: { xs: 'none', sm: 'flex' } },
};

const bottomStackProps: StackProps = {
  flexDirection: 'row',
  justifyContent: 'flex-end',
  sx: { marginTop: { xs: '20px', sm: '0px' } },
};

const questionNoMobileProps: StackProps = {
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  sx: { display: { xs: 'flex', sm: 'none' } },
};

const buttonDefaultStyles = {
  fontWeight: 600,
  height: '45px',
  width: '110px',
  marginLeft: '10px',
};

const primaryBtnProps: ButtonProps = {
  variant: 'contained',
  sx: {
    fontWeight: 600,
    height: '45px',
    gap: '10px',
    marginLeft: '10px',
    backgroundColor: '#1E293B',
    '&:hover': {
      backgroundColor: '#090c11',
    },
  },
};

const secondaryBtnProps: ButtonProps = {
  variant: 'outlined',
  color: 'inherit',
  sx: {
    ...buttonDefaultStyles,
    color: TP_COLORS.gray.darken,
    borderColor: TP_COLORS.gray.darken,
    backgroundColor: '#F8FAFC',
  },
};

const blackBtnProps: ButtonProps = {
  variant: 'contained',
  color: 'secondary',
  sx: {
    ...buttonDefaultStyles,
    color: '#F8FAFC',
    backgroundColor: TP_COLORS.gray.darken,
  },
};

const aiTypoProps: TypographyProps = {
  fontWeight: '600',
  sx: {
    background: 'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
};

const markingSchemeTypoProps: TypographyProps = {
  fontSize: '16px',
  fontWeight: 'bold',
  marginTop: '20px',
  display: 'flex',
  alignItems: 'center',
};

const TheoryQuestions: React.FC<TheoryQuestionsProps> = ({ questionList }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  // Store
  const questionState = useAppSelector(getQuestionState);
  const { lpAccess } = useAppSelector(getAccessLimitState);
  const authState = useAppSelector(getAuthState);
  const authUser = authState.user;

  // State
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [answer, setAnswer] = useState<string>('');
  const [answers, setAnswers] = useState<{ [key: string]: string }>({});
  const [selectedQuestion, setSelectedQuestion] = useState<
    IQuestion | undefined
  >(undefined);

  const selectedQuestionIndex =
    questionList.length && selectedQuestion
      ? questionList.findIndex(
          (questionItem) => questionItem._id === selectedQuestion._id
        )
      : -1;

  useEffect(() => {
    if (questionList.length) {
      setSelectedQuestion((prevSelectedQuestion) => {
        if (!prevSelectedQuestion) return questionList[0];

        const foundQuestion = questionList.find(
          (question) => question._id === prevSelectedQuestion._id
        );

        return foundQuestion || questionList[0];
      });
    }
  }, [questionList]);

  useEffect(() => {
    if (selectedQuestion) {
      setAnswer(answers[selectedQuestion._id] || '');
    }
  }, [selectedQuestion]);

  const handleScrollToQuestions = () => {
    const practiceQuestionsElement = document.getElementById(
      'practice-questions'
    );
    if (practiceQuestionsElement) {
      const offset = 90;
      const elementPosition =
        practiceQuestionsElement.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const handleNext = () => {
    if (
      questionList.length &&
      selectedQuestionIndex < questionList.length - 1
    ) {
      setSelectedQuestion(questionList[selectedQuestionIndex + 1]);
      setIsSubmitted(false);
    }
    handleScrollToQuestions();
  };

  const handleSubmit = () => {
    if (selectedQuestion?._id) {
      dispatch(
        getAIMarking(
          answer
            ? { questionId: selectedQuestion._id, answer }
            : { questionId: selectedQuestion._id }
        )
      );
      setIsSubmitted(true);
    }
  };

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswer(e.target.value);
    if (selectedQuestion) {
      setAnswers((prevAnswers) => ({
        ...prevAnswers,
        [selectedQuestion._id]: e.target.value,
      }));
    }
  };

  const handlePrev = () => {
    if (questionList.length && selectedQuestionIndex > 0) {
      setSelectedQuestion(questionList[selectedQuestionIndex - 1]);
      setIsSubmitted(true);
    }
    handleScrollToQuestions();
  };

  const handleFinish = () => {
    setIsFinished(true);
    if (
      authUser &&
      (authUser.role.name === 'Student' || authUser.role.name === 'Tutor') &&
      params.subtopicId
    ) {
      dispatch(
        saveStudentProgress({
          subTopicId: params.subtopicId,
          practiceQuestionCompletion: true,
        })
      );
    }
    handleScrollToQuestions();
  };

  // eslint-disable-next-line no-shadow
  const getBorderColor = (score: number | null, maxMarks: number) => {
    if (score === 0) return '#ff7070';
    if (score !== null && score < maxMarks) return '#FFAB00';
    if (score === maxMarks) return '#22C55E';
    return 'transparent';
  };

  const getBackgroundColor = (score: number | null, maxMarks: number) => {
    if (score === 0) return '#fff5f5';
    if (score !== null && score < maxMarks) return '#FFF5CC33';
    if (score === maxMarks) return '#e9fbf0';
    return 'transparent';
  };

  const extractScore = (openAimarking: string | undefined) => {
    if (!openAimarking) return null;
    const match = openAimarking.match(
      /(?:Your score (?:is|:)\s*|Marks:\s*)?(\d+(\.\d+)?)\/\d+(\.\d+)?|(\d+(\.\d+)?) out of \d+(\.\d+)?|^(\d+(\.\d+)?)$/
    );
    return match ? parseFloat(match[1] || match[4] || match[7]) : null;
  };

  const score = extractScore(selectedQuestion?.openAimarking);
  const borderColor = getBorderColor(score, selectedQuestion?.marks || 0);
  const backgroundColor = getBackgroundColor(
    score,
    selectedQuestion?.marks || 0
  );

  const accessState = !lpAccess && selectedQuestionIndex === 2;

  return (
    <Box id="practice-questions" marginBottom="40px">
      {!isFinished ? (
        <>
          {selectedQuestion && (
            <Box sx={accessState ? { position: 'relative' } : {}}>
              <Box
                {...boxProps}
                style={
                  accessState
                    ? {
                        position: 'relative',
                        opacity: 0.5,
                        pointerEvents: 'none',
                      }
                    : {}
                }
              >
                <Stack {...topStackProps}>
                  <Chip
                    label={`Question ${selectedQuestionIndex + 1}`}
                    style={{
                      ...chipStyles,
                      backgroundColor: TP_COLORS.border.color1,
                      color: TP_COLORS.gray.darken,
                    }}
                  />
                  <Box>
                    <Chip
                      label={selectedQuestion.difficulty}
                      color={getQuestionDifficultyChipColor(
                        selectedQuestion.difficulty
                      )}
                      style={chipStyles}
                    />
                    <Chip
                      label={`${selectedQuestion.marks} Mark(s)`}
                      style={marksChipStyles}
                    />
                  </Box>
                </Stack>
                <div>
                  <MarkdownRenderer
                    content={selectedQuestion.theoryQuestion?.question || ''}
                  />
                </div>
                <Box sx={{ margin: '10px' }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    value={answer}
                    onChange={handleAnswerChange}
                    placeholder="Type your answer here ..."
                    disabled={!!selectedQuestion.openAimarking}
                    InputProps={{
                      style: {
                        borderRadius: '10px',
                        backgroundColor: `${backgroundColor}`,
                        border: `1.5px solid ${borderColor}`,
                      },
                    }}
                  />
                  {selectedQuestion.openAimarking && (
                    <Typography {...markingSchemeTypoProps}>
                      Marking Scheme:
                      <img
                        src={`${process.env.PUBLIC_URL}/images/backgrounds/star.png`}
                        alt="ai-logo"
                        style={{
                          width: '18px',
                          height: '14px',
                          marginLeft: '10px',
                        }}
                      />
                      <Typography
                        fontSize="14px"
                        marginLeft="5px"
                        component="span"
                        {...aiTypoProps}
                      >
                        AI Generated
                      </Typography>
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    {questionState.status === 'loading' && <TableLoader />}
                  </Box>
                  {selectedQuestion.openAimarking && (
                    <Box
                      sx={{
                        marginTop: '20px',
                        position: 'relative',
                        padding: '2px',
                        borderRadius: '20px',
                        background:
                          'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
                      }}
                    >
                      <Box
                        sx={{
                          borderRadius: '18px',
                          background: TP_COLORS.white,
                          padding: '20px',
                        }}
                      >
                        <MarkdownRenderer
                          content={selectedQuestion.openAimarking || ''}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
              {accessState && (
                <Button
                  variant="contained"
                  onClick={() =>
                    navigate(`${process.env.PUBLIC_URL}/dashboard/upgrade`)
                  }
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    background:
                      'linear-gradient(90deg, #DB86F8 0%, #006FD6 100%)',
                    color: 'white',
                    width: '250px',
                    height: '60px',
                    fontSize: '16px',
                    pointerEvents: 'auto',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    '&:hover': {
                      background:
                        'linear-gradient(90deg, #C076E0 0%, #0056A6 100%)',
                    },
                  }}
                  startIcon={<LockOpenRoundedIcon />}
                >
                  Unlock with Premium
                </Button>
              )}
            </Box>
          )}

          <Stack {...questionNoStackProps}>
            <Typography variant="subtitle2" fontWeight="600">
              {selectedQuestionIndex + 1} of {questionList.length}
            </Typography>
          </Stack>
          <Stack {...bottomStackProps}>
            <Box {...questionNoMobileProps}>
              <Typography variant="subtitle2" fontWeight="600">
                {selectedQuestionIndex + 1} of {questionList.length}
              </Typography>
            </Box>

            <Box display="flex">
              {questionList.length && selectedQuestionIndex > 0 ? (
                <Button {...secondaryBtnProps} onClick={handlePrev}>
                  Prev
                </Button>
              ) : null}

              {selectedQuestion?.openAimarking &&
              selectedQuestionIndex < questionList.length - 1 ? (
                <Button {...secondaryBtnProps} onClick={handleNext}>
                  Next
                </Button>
              ) : null}

              {!isSubmitted && !selectedQuestion?.openAimarking && (
                <Button {...primaryBtnProps} onClick={handleSubmit}>
                  AI Marker
                  <img
                    src={`${process.env.PUBLIC_URL}/images/backgrounds/star.png`}
                    alt="ai-logo"
                    style={{
                      width: '20px',
                      height: '18px',
                    }}
                  />
                </Button>
              )}

              {selectedQuestion?.openAimarking &&
                questionList.length - 1 === selectedQuestionIndex && (
                  <Button {...blackBtnProps} onClick={handleFinish}>
                    Finish
                  </Button>
                )}
            </Box>
          </Stack>
        </>
      ) : (
        <PracticeQuestionLastPage />
      )}
    </Box>
  );
};

export default TheoryQuestions;
